import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointEmploymentDataInput,
  type EnrollmentJointEmploymentData,
  type EnrollmentJointEmploymentSubmitInput,
  type EnrollmentJointEmploymentSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint Employment data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointEmploymentData>
 * @example const response = await getEnrollmentJointEmploymentData()
 */
export const getEnrollmentJointEmploymentData = async (
  enrollmentEmploymentSubmitInput?: EnrollmentJointEmploymentDataInput,
  context?: DefaultContext,
): Promise<EnrollmentJointEmploymentData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointEmploymentData',
      query: `mutation getEnrollmentJointEmploymentData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            applicationId
            path
            progress
            employmentStatus
            occupation
            name {
              firstName
              lastName
            }
          }
        }
      }`,
    },
    {
      input: enrollmentEmploymentSubmitInput ?? ({} as EnrollmentJointEmploymentDataInput),
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointEmploymentData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointEmploymentData = createQueryHook<
  EnrollmentJointEmploymentDataInput,
  EnrollmentJointEmploymentData
>(getEnrollmentJointEmploymentData)

/**
 * Submit Enrollment Joint Employment data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointEmploymentSubmitOutput>
 * @example const response = await submitEnrollmentJointEmploymentData();
 */
export const submitEnrollmentJointEmploymentData = async (
  enrollmentEmploymentSubmitInput: EnrollmentJointEmploymentSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointEmploymentSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointEmploymentData',
      query: `mutation submitEnrollmentJointEmploymentData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentEmploymentSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointEmploymentSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointEmploymentData = createMutationHook<
  EnrollmentJointEmploymentSubmitInput,
  EnrollmentJointEmploymentSubmitOutput
>(submitEnrollmentJointEmploymentData)
